import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './AchievementUnlockedToast.css';

const TITLE_COLORS = {
  white: '#e0e0e0',
  blue: '#5eb3f6',
  purple: '#b388ff',
  golden: '#ffd700'
};

/**
 * Shows "Achievement unlocked: [Title Name]" and why (e.g. "Winning a battle without taking damage").
 * Use when API returns achievementUnlocked: { titleId, titleName, color, achievementKey } or pending toast with achievementKey.
 */
const AchievementUnlockedToast = ({ titleName, color = 'golden', achievementKey, onClose, duration = 10000 }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (typeof playSoundEffect === 'function') {
      playSoundEffect('/sounds/achievement_unlocked.mp3', 0.6);
    }
    const timer = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!isVisible || !titleName) return null;

  const titleColor = TITLE_COLORS[color] || TITLE_COLORS.golden;
  const reasonKey = achievementKey ? `achievementUnlocked.reason.${achievementKey}` : null;
  const reasonText = reasonKey ? t(reasonKey) : null;
  const showReason = reasonText && reasonText !== reasonKey;
  const bgImage = `${process.env.PUBLIC_URL || ''}/images/achievement_unlocked.jpg`;

  return createPortal(
    <div
      className={`achievement-unlocked-toast ${isAnimating ? 'achievement-unlocked-toast-exit' : ''}`}
      onClick={handleClose}
      role="alert"
      style={{
        backgroundImage: `url("${bgImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="achievement-unlocked-toast-content">
        <span className="achievement-unlocked-toast-label">{t('achievementUnlocked.label')}</span>
        <span className="achievement-unlocked-toast-title" style={{ color: titleColor }}>{titleName}</span>
        {showReason && <span className="achievement-unlocked-toast-reason">{reasonText}</span>}
      </div>
      <button
        className="achievement-unlocked-toast-close"
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        aria-label="Close"
      >
        ×
      </button>
    </div>,
    document.body
  );
};

export default AchievementUnlockedToast;
